<template>
  <div ref="rootEl" class="status-history-record">
    <div class="metadata">
      <ellipsis-text class="name" :text="actionBy" />
      <div class="date">{{ statusDate }}</div>
      <label-component :text="displayStatus" kind="resource-status" :class="statusTagClass" />
    </div>

    <!-- Remarks section -->
    <template v-if="showRemark">
      <!-- Remarks Text -->
      <div v-safe-html="displayRemarks" class="content remarks" :class="remarksClass"></div>
      <!-- Truncation Actions -->

      <cv-link v-if="truncateRemarks" class="btn-toggle-truncation" @click="handleToggleTruncation">
        <!-- Read More / Read Less -->
        <span class="truncation-toggle">{{ truncateActionLabel }}</span>
      </cv-link>
    </template>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from "@utils/var.check";
import CGLessonApi from "@apis/cg.lesson.api";
import DateTimeFormatter from "@utils/datetime.formatter";
import DOMUtils from "@utils/dom.utils";
import appConfigs from "@configs/app";
import { ref, computed, onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";

// Props
const props = defineProps({
  resourceDomain: { type: String, required: true },
  record: { type: Object, required: true },
});

// Data
const truncationType = ref("primary");
const truncateRemarks = ref(true);
const isRemarksExpanded = ref(false);
const processedRemarks = ref(props.record.remarks || "");
const rootElRef = useTemplateRef<HTMLElement | null>("rootEl");

const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

// Computed
const isCgResource = computed(() => {
  return props.resourceDomain === "community-gallery";
});

const actionBy = computed(() => {
  return props.record.actionByName || "";
});

const resourceStatus = computed(() => {
  return props.record.resourceStatus || "";
});

const displayStatus = computed(() => {
  const status = resourceStatus.value.toUpperCase();
  if (!isCgResource.value) return status.replaceAll("_", " ");

  // case when it is a CG resource
  switch (status) {
    case "APPROVED":
      return "FEATURED";
    case "PENDING_APPROVAL":
      return "PENDING REVIEW";
    case "REJECTED":
      return "RETURNED";
    default:
      return status;
  }
});

const statusDate = computed(() => {
  return DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(props.record.statusDate) || "";
});

const displayRemarks = computed(() => {
  let displayRemarks = processedRemarks.value || "";
  displayRemarks = displayRemarks.replace(
    "{{duplicateFromLabel}}",
    t("lesson_duplicate_from_label"),
  );
  displayRemarks = displayRemarks.replace("{{replaceByLabel}}", t("lesson_replace_by"));
  displayRemarks = displayRemarks.replace("{{replaceLessonLabel}}", t("lesson_replaces"));
  displayRemarks = displayRemarks.replace("{{submittedByLabel}}", t("lesson_submitted_by"));
  displayRemarks = displayRemarks.replace(
    "{{viewCommunityResourceUrl}}",
    appConfigs.vleDomain + `/admin/community-gallery/module/view/`,
  );
  return displayRemarks;
});

const showRemark = computed(() => {
  return !isEmpty(displayRemarks.value);
});

const statusTagClass = computed(() => {
  return resourceStatus.value.trim().replaceAll("_", "-").toLowerCase();
});

const remarksClass = computed(() => {
  return [
    {
      collapsed: truncateRemarks.value && !isRemarksExpanded.value,
    },
    `${truncationType.value}-truncation`,
  ];
});

const truncateActionLabel = computed(() => {
  return isRemarksExpanded.value ? t("read_less") : t("read_more");
});

// Methods
const determineRemarksTruncation = () => {
  if (!showRemark.value) return;

  // remarksContainer should be in the collapsed state at this point,
  // in which we will check if its view height over its scroll height.
  const remarksContainer = DOMUtils.elOf(rootElRef.value, ".remarks")[0];
  if (remarksContainer.clientHeight < remarksContainer.scrollHeight) {
    // isRemarksExpanded.value should be true now, so no need to set it to true.
    // set default state of the truncation (expanded or collapsed)
    isRemarksExpanded.value = true;
  } else {
    // no need for truncation, turn it off.
    truncateRemarks.value = false;
  }
};

const handleToggleTruncation = () => {
  isRemarksExpanded.value = !isRemarksExpanded.value;
};

const processCGUrl = () => {
  const remark = props.record.remarks;
  const cgUrlIndex = remark && remark.indexOf("viewCommunityResourceUrl");
  if (cgUrlIndex > 0) {
    const dataResourceStr = "data-resource-uuid='";
    const dataResourceStrLength = dataResourceStr.length;
    const dataIndex = remark.indexOf(dataResourceStr) + dataResourceStrLength;
    const remarkSubStr = remark.substr(dataIndex);
    const endOfUuidIndex = remarkSubStr.indexOf("'");
    const resourceUuid = remarkSubStr.substr(0, endOfUuidIndex);

    CGLessonApi.fetchCGUuidByResourceId({
      resourceType: "LESSON",
      resourceUuid: resourceUuid,
    }).then((response) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        return;
      }
      // Update processedRemarks instead of mutating the prop
      processedRemarks.value = remark.replace("{{communityResourceUuid}}", response.data);
    }, passiveApiFailHandler);
  } else {
    // If no CG URL, just keep original remarks
    processedRemarks.value = remark;
  }
};

// Created
processCGUrl();

// Mounted
onMounted(() => {
  determineRemarksTruncation();
});
</script>

<style lang="scss">
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.status-history-record {
  position: relative;
  padding: $spacing-05 0;
  border-bottom: 1px solid $ui-03;

  .metadata {
    .name {
      @include carbon--type-style("productive-heading-01", true);
    }

    .date {
      @include carbon--type-style("helper-text-01", true);
      color: $text-02;
    }
  }

  .remarks {
    @include carbon--type-style("body-short-01", true);
    position: relative;
    margin-top: $spacing-03;
    word-break: break-word;

    &.collapsed {
      max-height: 5rem;
      overflow: hidden;

      &::before {
        content: "";
        position: absolute;
        height: 100%;
        width: 100%;
        background-image: $secondary-truncation;
        z-index: z-map("default");
      }
    }
  }
}
</style>
